"use client";

import { usePathname, useRouter } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import { useEffect, useState, useCallback, useRef } from "react";
import Navigation, { MobileHeader } from "~/components/layouts/Navigation";
import { ImpersonationBanner, IMPERSONATION_BANNER_HEIGHT } from "~/components/ui/impersonation-banner";
import { useImpersonation } from "~/lib/impersonation-context";
import { SessionMonitor } from "~/components/providers/SessionMonitor";

export function LayoutContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { data: session, status } = useSession();
  const { impersonationData, endImpersonation } = useImpersonation();
  const [isMounted, setIsMounted] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const hasRedirected = useRef(false);
  
  const publicAuthRoutes = [
    "/login",
    "/forgot-password",
    "/forgot-password/sent",
    "/reset-password",
  ];
  const isPublicPage = publicAuthRoutes.includes(pathname);
  const isImpersonating = impersonationData.isImpersonating;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isPublicPage || !isMounted) return;
    
    if (status === "unauthenticated" && !hasRedirected.current) {
      hasRedirected.current = true;
      endImpersonation();
      
      void signOut({ redirect: false }).then(() => {
        router.push(`/login?session=expired&callbackUrl=${encodeURIComponent(pathname)}`);
      });
    }
  }, [status, isPublicPage, isMounted, pathname, router, endImpersonation]);

  useEffect(() => {
    if (status === "authenticated") {
      hasRedirected.current = false;
    }
  }, [status]);

  const handleMobileMenuToggle = useCallback((isOpen?: boolean) => {
    setIsMobileMenuOpen(prev => isOpen ?? !prev);
  }, []);

  const handleOpenMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(true);
  }, []);

  if (isPublicPage) {
    return <>{children}</>;
  }

  if (!isMounted) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#1c3144]" />
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session && status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#1c3144]" />
          <p className="text-sm text-gray-600">Loading session...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Session Monitor - Warns before expiry and auto-redirects */}
      <SessionMonitor 
        showWarning={true}
        warningThresholdMinutes={5}
        checkIntervalSeconds={30}
        position="top-right"
      />

      {/* Impersonation Banner - Fixed at top when impersonating */}
      <ImpersonationBanner />
      
      <div 
        className="flex flex-col lg:flex-row overflow-hidden"
        style={{ 
          marginTop: isImpersonating ? `${IMPERSONATION_BANNER_HEIGHT}px` : 0,
          height: isImpersonating ? `calc(100vh - ${IMPERSONATION_BANNER_HEIGHT}px)` : '100vh',
        }}
      >
        {/* Mobile Header - Only visible on mobile */}
        <MobileHeader onMenuToggle={handleOpenMobileMenu} />
        
        {/* Navigation - Desktop sidebar + Mobile overlay */}
        <Navigation 
          isMobileMenuOpen={isMobileMenuOpen}
          onMobileMenuToggle={handleMobileMenuToggle}
        />
        
        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-white">
          {children}
        </main>
      </div>
    </>
  );
}
